import type { Credentials, SessionUser } from "./types"
import { defineStore } from "pinia"
import { computed, ref } from "vue"
import { clearTokens, getAccessToken, getRefreshToken, setTokens } from "@/shared/api/tokens"
import { authApi } from "../api/authApi"

const USER_KEY = "safy.admin.user"

function readUser(): SessionUser | null {
  const raw = localStorage.getItem(USER_KEY)
  if (!raw)
    return null
  try {
    return JSON.parse(raw) as SessionUser
  } catch {
    return null
  }
}

// Session is the single source of truth for "who is logged in". The router guard
// and the HTTP layer's session-expired handler both drive it; the sidebar and
// permission checks read from it.
export const useSessionStore = defineStore("session", () => {
  // Hydrate synchronously from localStorage so the guard has an answer on first
  // navigation (no auth flash on refresh). Tokens themselves live in
  // shared/api/tokens — this only mirrors their presence for reactivity.
  const hasToken = ref(Boolean(getAccessToken()))
  const user = ref<SessionUser | null>(readUser())

  const isAuthenticated = computed(() => hasToken.value)
  const permissions = computed(() => new Set(user.value?.permissions ?? []))

  /** Permission codes come from the roles the API attached to the admin. */
  function can(code: string): boolean {
    return permissions.value.has(code)
  }

  function setUser(next: SessionUser): void {
    user.value = next
    localStorage.setItem(USER_KEY, JSON.stringify(next))
  }

  function clear(): void {
    hasToken.value = false
    user.value = null
    clearTokens()
    localStorage.removeItem(USER_KEY)
  }

  async function login(credentials: Credentials): Promise<void> {
    const { accessToken, refreshToken, user: nextUser } = await authApi.login(credentials)
    setTokens({ accessToken, refreshToken })
    hasToken.value = true
    setUser(nextUser)
  }

  // Best-effort server-side revoke: the local session is dropped either way, so a
  // network failure can't strand the user in a signed-in UI.
  async function logout(): Promise<void> {
    const refreshToken = getRefreshToken()
    if (refreshToken) {
      try {
        await authApi.logout(refreshToken)
      } catch {}
    }
    clear()
  }

  /** Drop the local session without calling the server (token already rejected). */
  function expire(): void {
    clear()
  }

  // Revalidate a persisted token on boot and refresh roles/permissions. On
  // failure the session is cleared so the guard bounces to /login.
  async function restore(): Promise<void> {
    if (!hasToken.value)
      return
    try {
      setUser(await authApi.me())
    } catch {
      clear()
    }
  }

  return { user, isAuthenticated, permissions, can, login, logout, expire, restore }
})
